import React, { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChatHistory } from "../ChatHistory";
import { Tooltip, TooltipTrigger, TooltipContent } from "@src/lib/ui/Tooltip";
import { Button, ButtonProps } from "@src/lib/ui/Button";
import MsgClock1Icon from "@src/lib/icons/18px/msg-clock-1";
import { AiChatType } from "@src/types.generated";
import { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";
import { cn } from "@src/lib/utils/cn";

interface HeaderIconButtonProps {
  onClick?: () => void;
  tooltip: string;
  children: React.ReactNode;
  variant?: ButtonProps["variant"];
  className?: string;
}

const HeaderIconButton: React.FC<HeaderIconButtonProps> = ({
  onClick,
  tooltip,
  children,
  variant = "ghost",
  className = "",
}) => (
  <Tooltip delayDuration={0}>
    <TooltipTrigger asChild>
      <Button
        onClick={onClick}
        variant={variant}
        size="icon-xs"
        className={cn(className)}
        tabIndex={-1} // Prevents the tooltip from a auto-opening when opening a dialog.
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{tooltip}</TooltipContent>
  </Tooltip>
);

export type ChatHistoryIconButtonProps = {
  filters: {
    chatType: AiChatType;
  };
  onClickPreviousChat: (chat: AiChatFragment) => void;
  variant?: ButtonProps["variant"];
  className?: string;
  tooltip?: string;
};

export const ChatHistoryIconButton: React.FC<ChatHistoryIconButtonProps> = ({
  filters,
  onClickPreviousChat,
  variant = "ghost",
  className = "",
  tooltip = "View chat history",
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal>
      <Popover.Trigger asChild>
        <div>
          <HeaderIconButton
            tooltip={tooltip}
            variant={variant}
            className={className}
          >
            <MsgClock1Icon className="size-3.5" />
          </HeaderIconButton>
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          className="z-50 max-h-[500px] w-[320px] rounded-lg border border-gray-200 bg-white p-0 shadow-lg focus:outline-none"
        >
          <ChatHistory
            filters={filters}
            onClickPreviousChat={(x) => {
              onClickPreviousChat(x);
              setOpen(false);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default ChatHistoryIconButton;
